import { AdGrid } from '@/components/AdGrid';
import { useAdDetailBackRoute } from '@/components/AdPage/AdBackLink';
import { Filter } from '@/components/Filter';
import { Pagination } from '@/components/Pagination';
import { Section } from '@/components/Section';
import { Text } from '@/components/Text';
import {
  AdType,
  getAds,
  type AdCategoryCountItemType,
  type AdFilterType,
  type AdItemType,
} from '@/resource/ad';
import { PaginatorType } from '@/resource/paginator';
import { objectFromEntries } from '@/util/objectFromEntries';
import { filterPairsValidator, parseFilters } from '@/util/parseFilters';
import { staticProps } from '@/util/staticProps';
import { GetStaticPaths } from 'next';
import { NextSeo } from 'next-seo';
import z from 'zod';
import { Nav } from './Nav'; // overview specific nav.
import { useEffect } from 'react';

type Props = {
  items: AdItemType[];
  filter: AdFilterType;
  categories: AdCategoryCountItemType[];
  paginator: PaginatorType;
  adType: AdType;
};

export function AdOverview({
  items,
  filter,
  categories,
  paginator,
  adType,
}: Props) {
  const adLabel = adType === 'offer' ? 'Aanbod' : 'Vraag';
  const overviewType = `market_${adType}s` as const;
  const filterCategoryIds = filter.category_ids || [];
  const selected = categories
    .filter((category) => filterCategoryIds.includes(category.id))
    .map((category) => category.name);

  const title = adLabel;
  const description =
    selected.length > 0 ? selected.join(', ') : 'Alle categorieën';

  // Set the correct backroute to use on the ad detail page.
  useEffect(
    () =>
      useAdDetailBackRoute.setState({ route: { type: overviewType, filter } }),
    [overviewType, filter]
  );

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          title,
          description,
        }}
      />

      <Section>
        <Nav adType={adType} />
      </Section>

      <Filter filter={filter} categories={categories} adType={adType} />

      <Section>
        <h2>{adLabel}</h2>

        <AdGrid items={items} filterCategoryIds={filter.category_ids || []} />

        {items.length === 0 && (
          <Text align="center" balance>
            Er {adType === 'offer' ? 'is geen aanbod' : 'zijn geen vragen'}{' '}
            gevonden binnen de opgegeven criteria.
          </Text>
        )}

        <Pagination
          offset={paginator.offset}
          last={paginator.last}
          route={(page) => ({
            type: overviewType,
            filter: {
              ...filter,
              page,
            },
          })}
        />
      </Section>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticPropsFor = (adType: AdType) =>
  staticProps
    .params(
      z.object({
        pairs: filterPairsValidator.optional(),
      })
    )
    .resolve<Props>(async ({ pairs = [] }) => {
      const filters = parseFilters(pairs);

      if (!Array.isArray(filters)) {
        return {
          notFound: true,
          revalidate: 10,
        };
      }

      const filter = objectFromEntries(filters);
      const { categories, data, paginator } = await getAds({
        type: adType,
        ...filter,
      });

      return {
        props: {
          items: data,
          categories,
          filter,
          paginator,
          adType,
        },
        revalidate: 10,
      };
    });
